import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  VStack,
  Stack,
  Flex,
  Spinner,
  HStack,
} from "@chakra-ui/react";
import HeaderTools from "../../components/HeaderTools";
import api from "../../services/api";
import { toast } from "react-toastify";

/* ======================
   Helpers de data
====================== */
const getWeekNumber = (date) => {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
};

export default function Ranking() {
  const [selectedPeriod, setSelectedPeriod] = useState("semanal");
  const [currentWeek, setCurrentWeek] = useState(getWeekNumber(new Date()));
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const periods = [
    { id: "diario", label: "diário" },
    { id: "semanal", label: "semanal" },
    { id: "mensal", label: "mensal" },
    { id: "anual", label: "anual" },
  ];

  const handlePeriodChange = (periodId) => {
    setSelectedPeriod(periodId);
  };

  /* ======================
     Buscar pedidos
  ====================== */
  useEffect(() => {
    const getOrders = async () => {
      try {
        setLoading(true);
        const response = await api.get("/orders");
        setOrders(response.data);
      } catch {
        toast.error(
          "Não foi possível buscar os seus pedidos, entre em contato com o suporte.",
        );
      } finally {
        setLoading(false);
      }
    };

    getOrders();
  }, []);

  /* ======================
     Ranking por período
  ====================== */
  const ranking = useMemo(() => {
    const now = new Date();

    const filtered = orders.filter((order) => {
      const orderDate = new Date(order.createdAt);

      if (selectedPeriod === "diario") {
        return (
          orderDate.getDate() === now.getDate() &&
          orderDate.getMonth() === now.getMonth() &&
          orderDate.getFullYear() === now.getFullYear()
        );
      }

      if (selectedPeriod === "semanal") {
        return (
          getWeekNumber(orderDate) === currentWeek &&
          orderDate.getFullYear() === now.getFullYear()
        );
      }

      if (selectedPeriod === "mensal") {
        return (
          orderDate.getMonth() === now.getMonth() &&
          orderDate.getFullYear() === now.getFullYear()
        );
      }

      if (selectedPeriod === "anual") {
        return orderDate.getFullYear() === now.getFullYear();
      }

      return true;
    });

    // ✅ Agrupar por CLIENTE (comprador)
    const grouped = {};

    filtered.forEach((order) => {
      const clientId = order.client_id;

      if (!grouped[clientId]) {
        grouped[clientId] = {
          clientId,
          clientName: order.client?.name || "Cliente não identificado",
          total: 0,
          orders: 0,
        };
      }

      grouped[clientId].total += Number(order.totalValue || 0);
      grouped[clientId].orders += 1;
    });

    return Object.values(grouped).sort((a, b) => b.total - a.total);
  }, [orders, selectedPeriod, currentWeek]);

  return (
    <>
      <HeaderTools title="Ranking" />

      <Box p={{ base: "16px", md: "20px" }} bg="gray.50" minH="100vh">
        {/* =====================
            Seletor de período
        ===================== */}
        <Stack
          direction="row"
          mb={6}
          bg="white"
          p={1}
          borderRadius="8px"
          shadow="sm"
        >
          {periods.map((period) => (
            <Box
              key={period.id}
              flex="1"
              bg={selectedPeriod === period.id ? "gray.800" : "transparent"}
              color={selectedPeriod === period.id ? "white" : "gray.600"}
              py={3}
              borderRadius="6px"
              cursor="pointer"
              textAlign="center"
              fontWeight={selectedPeriod === period.id ? "semibold" : "normal"}
              _hover={{
                bg: selectedPeriod === period.id ? "gray.700" : "gray.100",
              }}
              onClick={() => handlePeriodChange(period.id)}
            >
              {period.label}
            </Box>
          ))}
        </Stack>

        {selectedPeriod === "semanal" && (
          <Text mb={6} fontWeight="semibold">
            Semana {currentWeek}
          </Text>
        )}

        {/* =====================
            Conteúdo
        ===================== */}
        <Box bg="white" borderRadius="12px" p={6} shadow="sm">
          {loading && (
            <Flex justify="center">
              <Spinner />
            </Flex>
          )}

          {!loading && ranking.length === 0 && (
            <VStack spacing={4} py={10}>
              <Text fontSize="32px">📊</Text>
              <Text color="gray.500">Nenhuma venda no período</Text>
            </VStack>
          )}

          {!loading && ranking.length > 0 && (
            <VStack spacing={4} align="stretch">
              {ranking.map((item, index) => (
                <Flex
                  key={item.clientId}
                  justify="space-between"
                  align="center"
                  p={4}
                  bg={index === 0 ? "yellow.50" : "gray.50"}
                  borderRadius="8px"
                >
                  <HStack spacing={4}>
                    <Text fontWeight="bold">{index + 1}º</Text>
                    <Box>
                      <Text fontWeight="semibold">{item.clientName}</Text>
                      <Text fontSize="sm" color="gray.500">
                        {item.orders} {item.orders > 1 ? "pedidos" : "pedido"}
                      </Text>
                    </Box>
                  </HStack>

                  <Text fontWeight="bold">
                    R${" "}
                    {item.total.toLocaleString("pt-BR", {
                      minimumFractionDigits: 2,
                    })}
                  </Text>
                </Flex>
              ))}
            </VStack>
          )}
        </Box>
      </Box>
    </>
  );
}
